'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Portal } from '@axon/ui';

import {
  notificationManager,
  NotificationPreferences,
  InAppNotification,
  AlertBannerOptions,
} from '@/lib/notification-manager';
import { AlertBanner } from '@/components/alert-banner';
import { NotificationCenter } from '@/components/notification-center';

interface NotificationContextValue {
  preferences: NotificationPreferences;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => void;
  notifications: InAppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  showNotificationCenter: boolean;
  setShowNotificationCenter: (show: boolean) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<NotificationPreferences>(
    notificationManager.getPreferences()
  );
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alertBanners, setAlertBanners] = useState<AlertBannerOptions[]>([]);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);

  // Define callbacks before useEffects
  const updateNotifications = useCallback(() => {
    const allNotifications = notificationManager.getInAppNotifications();
    setNotifications(allNotifications);
    setUnreadCount(notificationManager.getUnreadCount());
  }, []);

  // Load notifications on mount
  useEffect(() => {
    notificationManager.loadInAppNotifications();
    updateNotifications();
  }, [updateNotifications]);

  // Subscribe to notification changes
  useEffect(() => {
    const unsubscribe = notificationManager.onNotification(() => {
      updateNotifications();
    });
    return unsubscribe;
  }, [updateNotifications]);

  // Subscribe to alert banner changes
  useEffect(() => {
    const unsubscribe = notificationManager.onAlertChange((alerts) => {
      setAlertBanners(alerts);
    });
    return unsubscribe;
  }, []);

  const updatePreferences = useCallback((newPreferences: Partial<NotificationPreferences>) => {
    notificationManager.updatePreferences(newPreferences);
    setPreferences(notificationManager.getPreferences());
  }, []);

  const markAsRead = useCallback(
    (id: string) => {
      notificationManager.markAsRead(id);
      updateNotifications();
    },
    [updateNotifications]
  );

  const markAllAsRead = useCallback(() => {
    notificationManager.markAllAsRead();
    updateNotifications();
  }, [updateNotifications]);

  const clearAll = useCallback(() => {
    notificationManager.clearAll();
    updateNotifications();
  }, [updateNotifications]);

  const value: NotificationContextValue = {
    preferences,
    updatePreferences,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    showNotificationCenter,
    setShowNotificationCenter,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Alert Banners */}
      <Portal>
        <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 space-y-2">
            {alertBanners.map((alert) => (
              <div key={alert.id} className="pointer-events-auto">
                <AlertBanner
                  {...alert}
                  onDismiss={() => notificationManager.dismissAlertBanner(alert.id)}
                />
              </div>
            ))}
          </div>
        </div>
      </Portal>

      {/* Notification Center */}
      {showNotificationCenter && (
        <NotificationCenter onClose={() => setShowNotificationCenter(false)} />
      )}
    </NotificationContext.Provider>
  );
}
